import {createStore} from "vuex";
import VuexPersistedstate from 'vuex-persistedstate'
import {changeTopSafeAreaColor, changeBottomSafeAreaColor} from "@/tarot_box/helper/native_api";
import {getSafeAreaColorCode, calculateOverLayColor} from "@/common/utils";

// 캐시 만료 시간 (5분)
const CACHE_EXPIRY_TIME = 5 * 60 * 1000;

export default createStore({
    state: {
        user: null,
        myPageUserInfo: {
            displayName: '',
            cloverBalance: 0,
            referralCode: '',
            reminderTime: ''
        },
        tarot_boxes: [],  // 기존 tarotBoxes를 tarot_boxes로 변경
        promotion_banners: [],
        icon_categories: [],
        home_sections: [],
        ranking_subject_titles: [],
        searchBarContents: {}, // searchBarContents 추가
        feedbackBoxVisible: true,
        safeAreaColorTopHistory: ['#FFF9F9F9'],
        safeAreaColorBottomHistory: ['#FF171B2B'],
        tempInputData: [],
        tempCardData: "",
        paywall: {
            android: [],
            ios: []
        },
        subscriptionPaywall: {
            android: [],
            ios: []
        },
        utmReferrer: null,
        recentSearches: [],
        cachedSearches: {},
        historyData: [],
        historyDataCache: {
            data: [],
            timestamp: null,
            userId: null, // 사용자별 캐시 구분
        },
        historyLoading: false,
    },
    mutations: {
        setUser(state, user) {
            state.user = {
                ...state.user,
                ...user,
            };
        },
        UPDATE_MY_PAGE_USER_INFO(state, payload) {
            state.myPageUserInfo = { ...state.myPageUserInfo, ...payload };
        },
        setTempInput(state, tempInputData) {
            state.tempInputData = tempInputData;
        },
        setTempCard(state, tempCardData) {
            state.tempCardData = tempCardData;
        },
        setTarotBoxes(state, tarot_boxes) {
            state.tarot_boxes = tarot_boxes;
        },
        setDailyTarotBox(state, dailyTarotBox) {
            state.dailyTarotBox = dailyTarotBox;
        },
        addDailyTarotBoxToTarotBoxes(state) {
            if (state.dailyTarotBox && !state.tarot_boxes.some(box => box.id === state.dailyTarotBox.id)) {
                state.tarot_boxes.push(state.dailyTarotBox);
            }
        },
        setPromotionBanners(state, promotion_banners) {
            state.promotion_banners = promotion_banners;
        },
        setIconCategories(state, icon_categories) {
            state.icon_categories = icon_categories;
        },
        setHomeSections(state, home_sections) {
            state.home_sections = home_sections;
        },
        setRankingSubjectTitles(state, ranking_subject_titles) {
            state.ranking_subject_titles = ranking_subject_titles;
        },
        setSearchBarContents(state, searchBarContents) {
            state.searchBarContents = searchBarContents;
        },
        initSafeAreaColorHistory(state) {
            state.safeAreaColorTopHistory = ['#FFF9F9F9'];
            state.safeAreaColorBottomHistory = ['#FF171B2B'];
            changeTopSafeAreaColor('#FFF9F9F9');
            changeBottomSafeAreaColor('#FF171B2B');
        },
        updateRecommendList(state, recommendList) {
            let section = state.home_sections.find(section => section.sectionType === "recommend");
            if (section) {
                section.tarotBoxIds = recommendList;
            }
        },
        setPaywall(state, paywall) {
            state.paywall = paywall;
        },
        setSubscriptionPaywall(state, subscriptionPaywall) {
            state.subscriptionPaywall = subscriptionPaywall;
        },
        setUtmReferrer(state, referrer) {
            state.utmReferrer = referrer;
        },
        ADD_RECENT_SEARCH(state, query) {
            // 이미 존재하는 검색어라면 제거
            state.recentSearches = state.recentSearches.filter(item => item !== query);

            // 새 검색어 추가
            state.recentSearches.unshift(query);

            // 최대 5개까지만 유지
            if (state.recentSearches.length > 5) {
                state.recentSearches.pop();
            }
        },
        REMOVE_RECENT_SEARCH(state, query) {
            state.recentSearches = state.recentSearches.filter(item => item !== query);
        },
        CLEAR_RECENT_SEARCHES(state) {
            state.recentSearches = [];
        },
        // 캐시 저장 mutation 수정 - 타임스탬프 추가
        CACHE_SEARCH_RESULTS(state, { query, results }) {
            state.cachedSearches = {
                ...state.cachedSearches,
                [query]: {
                    results,
                    timestamp: Date.now() // 현재 시간을 타임스탬프로 저장
                }
            };
        },
        // 캐시 정리 mutation 추가
        CLEAR_EXPIRED_CACHE(state) {
            const now = Date.now();

            // 만료된 캐시 항목 제거
            const updatedCache = {};
            Object.keys(state.cachedSearches).forEach(key => {
                const cacheItem = state.cachedSearches[key];
                if (now - cacheItem.timestamp < CACHE_EXPIRY_TIME) {
                    updatedCache[key] = cacheItem;
                }
            });

            state.cachedSearches = updatedCache;
        },
        SET_HISTORY_DATA(state, { data, userId }) {
            state.historyData = data;
            state.historyDataCache = {
                data: data,
                timestamp: Date.now(),
                userId: userId
            };
        },
        SET_HISTORY_LOADING(state, isLoading) {
            state.historyLoading = isLoading;
        },
        RESTORE_HISTORY_FROM_CACHE(state, userId) {
            // 같은 사용자의 캐시이고 만료되지 않았으면 복원
            if (state.historyDataCache.userId === userId &&
                state.historyDataCache.timestamp &&
                Date.now() - state.historyDataCache.timestamp < CACHE_EXPIRY_TIME) {
                state.historyData = state.historyDataCache.data;
            }
        },
        UPDATE_HISTORY_ITEM(state, updatedItem) {
            // 특정 히스토리 아이템만 업데이트 (예: 결제 완료 시)
            const index = state.historyData.findIndex(item => item.id === updatedItem.id);
            if (index !== -1) {
                state.historyData.splice(index, 1, updatedItem);
                // 캐시도 업데이트
                if (state.historyDataCache.data) {
                    const cacheIndex = state.historyDataCache.data.findIndex(item => item.id === updatedItem.id);
                    if (cacheIndex !== -1) {
                        state.historyDataCache.data.splice(cacheIndex, 1, updatedItem);
                    }
                }
            }
        },
        REMOVE_HISTORY_ITEM(state, itemId) {
            // 히스토리 아이템 삭제
            state.historyData = state.historyData.filter(item => item.id !== itemId);
            // 캐시에서도 삭제
            if (state.historyDataCache.data) {
                state.historyDataCache.data = state.historyDataCache.data.filter(item => item.id !== itemId);
            }
        },
        CLEAR_HISTORY_CACHE(state) {
            state.historyDataCache = {
                data: [],
                timestamp: null,
                userId: null
            };
        },
    },
    actions: {
        setUserData(context, userInfo) {
            const user = {
                userId: userInfo.user_id,
                phoneNumber: userInfo.phone_number,
                userNickname: userInfo.user_nickname,
                webReferrer: userInfo.web_referrer,
            };
            context.commit('setUser', user);
        },
        updateMyPageUserInfo({ commit }, newUserInfo) {
            return new Promise((resolve) => {
                commit('UPDATE_MY_PAGE_USER_INFO', newUserInfo);
                resolve(true);
            });
        },
        setFeedbackBoxData({commit}, isVisible) {
            commit('setFeedbackBox', isVisible);
        },
        setTempInputData(context, tempInputData) {
            context.commit('setTempInput', tempInputData);
        },
        setTempCardData(context, tempCardData) {
            context.commit('setTempCard', tempCardData);
        },
        setTarotBoxesData(context, tarot_boxes) {
            const mappedTarotBoxes = tarot_boxes.map(box => ({
                id: box.id,
                title: box.title,
                subTitle: box.sub_title,
                category: box.category,
                tags: box.tags,
                tagImageUrl: box.tag_image_url,
                promotionImgUrl: box.promotion_img_url,
                thumbnailImgUrl: box.thumbnail_v2_img_url,
                categoryPriority: box.category_priority,
                description: box.description,
                tableOfContents: box.table_of_contents,
                inputPageUrl: box.input_page_url,
                rankingScore: box.ranking_score,
                originalPrice: box.original_price,
                salePrice: box.sale_price,
                priority: box.priority,
                boxType: box.box_type,
                appPermissions: box.app_permissions,
                screenShotImgUrlList: box.screen_shot_img_url_list,
                descImgUrl: box.desc_img_url,
                tarotSpread: box.tarot_spread,
                relatedBoxIds: box.related_box_ids,
                //231221 추가
                subjectColor: box.subject_color,
                subjectTitle: box.subject_title,
                //240112 추가
                feedbackSection: box.feedback_section, // 추가
                userReviews: box.user_reviews, // 추가
                reactionStats: box.reaction_stats, // 추가
                reviewReward: box.review_reward, // 추가
                reactionReward: box.reaction_reward, // 추가
                salePriceWon: box.sale_price_won, // 240219 추가
                originalPriceWon: box.original_price_won, // 240219 추가
                reviewCount: box.review_count // 240408 추가
            }));
            context.commit('setTarotBoxes', mappedTarotBoxes);
        },
        setSearchBarContentsData(context, searchBarContents) {
            context.commit('setSearchBarContents', searchBarContents);
        },
        setDailyTarotBoxData(context, dailyTarotBox) {
            const mappedDailyTarotBox = {
                id: dailyTarotBox.id,
                title: dailyTarotBox.title,
                subTitle: dailyTarotBox.sub_title,
                category: dailyTarotBox.category,
                tags: dailyTarotBox.tags,
                tagImageUrl: dailyTarotBox.tag_image_url,
                promotionImgUrl: dailyTarotBox.promotion_img_url,
                thumbnailImgUrl: dailyTarotBox.thumbnail_img_url,
                thumbnailV2ImgUrl: dailyTarotBox.thumbnail_v2_img_url,
                categoryPriority: dailyTarotBox.category_priority,
                subjectTitle: dailyTarotBox.subject_title,
                subjectColor: dailyTarotBox.subject_color,
                description: dailyTarotBox.description,
                tableOfContents: dailyTarotBox.table_of_contents,
                inputPageUrl: dailyTarotBox.input_page_url,
                rankingScore: dailyTarotBox.ranking_score,
                reviewReward: dailyTarotBox.review_reward,
                reactionReward: dailyTarotBox.reaction_reward,
                originalPrice: dailyTarotBox.original_price,
                salePrice: dailyTarotBox.sale_price,
                priority: dailyTarotBox.priority,
                boxType: dailyTarotBox.box_type,
                originalPriceWon: dailyTarotBox.original_price_won,
                salePriceWon: dailyTarotBox.sale_price_won,
                appPermissions: dailyTarotBox.app_permissions,
                screenShotImgUrlList: dailyTarotBox.screen_shot_img_url_list,
                descImgUrl: dailyTarotBox.desc_img_url,
                tarotSpread: dailyTarotBox.tarot_spread,
                feedbackSection: dailyTarotBox.feedback_section
            };
            context.commit('setDailyTarotBox', mappedDailyTarotBox);
            context.commit('addDailyTarotBoxToTarotBoxes');
            context.commit('setTarotBoxes', [...context.state.tarot_boxes]);
            console.log('completed_up_daily_tarot_box')
        },
        setPromotionBannersData(context, promotion_banners) {
            const mappedPromotionBanners = promotion_banners.map(banner => ({
                id: banner.id,
                title: banner.title,
                imageUrl: banner.image_url,
                linkType: banner.link_type,
                linkUrl: banner.link_url,
                priority: banner.priority,
                status: banner.status,
                createdAt: banner.created_at,
                updatedAt: banner.updated_at,
                app: banner.app,
                web: banner.web,
            }));
            context.commit('setPromotionBanners', mappedPromotionBanners);
        },
        setIconCategoriesData(context, icon_categories) {
            const mappedIconCategories = icon_categories.map(category => ({
                id: category.id,
                title: category.title,
                linkType: category.link_type,
                tarotBoxCategories: category.tarot_box_categories,
                imageUrl: category.image_url,
                priority: category.priority,
                status: category.status,
                createdAt: category.created_at,
                updatedAt: category.updated_at,
                //231221 추가
                tarotBoxSubjectTitle: category.tarot_box_subject_title,
                linkUrl: category.link_url,
                app: category.app,
                web: category.web,
            }));
            context.commit('setIconCategories', mappedIconCategories);
        },
        setHomeSectionsData(context, home_sections) {
            const mappedHomeSections = home_sections.map(section => ({
                id: section.id,
                title: section.title,
                sectionType: section.section_type,
                tarotBoxIds: section.tarot_box_ids,
                priority: section.priority,
                status: section.status,
            }));
            context.commit('setHomeSections', mappedHomeSections);
        },
        setRankingSubjectTitlesData(context, ranking_subject_titles) {
            context.commit('setRankingSubjectTitles', ranking_subject_titles);
        },
        addSafeAreaColorTop(context, color) {
            if (color.startsWith('-')) {
                color = getSafeAreaColorCode(color);
            } else if (color === 'overlay') {
                color = calculateOverLayColor(context.state.safeAreaColorTopHistory[context.state.safeAreaColorTopHistory.length - 1]);
            }
            context.state.safeAreaColorTopHistory.push(color);
            const topColor = context.state.safeAreaColorTopHistory[context.state.safeAreaColorTopHistory.length - 1];
            changeTopSafeAreaColor(topColor);
        },
        addSafeAreaColorBottom(context, color) {
            if (color.startsWith('-')) {
                color = getSafeAreaColorCode(color);
            } else if (color === 'overlay') {
                color = calculateOverLayColor(context.state.safeAreaColorBottomHistory[context.state.safeAreaColorBottomHistory.length - 1]);
            }
            context.state.safeAreaColorBottomHistory.push(color);
            const bottomColor = context.state.safeAreaColorBottomHistory[context.state.safeAreaColorBottomHistory.length - 1];
            changeBottomSafeAreaColor(bottomColor);
        },
        previousSafeAreaColorTop(context) {
            if(context.state.safeAreaColorTopHistory.length <= 1) return;
            context.state.safeAreaColorTopHistory.pop();
            const topColor = context.state.safeAreaColorTopHistory[context.state.safeAreaColorTopHistory.length - 1];
            changeTopSafeAreaColor(topColor);
        },
        previousSafeAreaColorBottom(context) {
            if(context.state.safeAreaColorBottomHistory.length <= 1) return;
            context.state.safeAreaColorBottomHistory.pop();
            const bottomColor = context.state.safeAreaColorBottomHistory[context.state.safeAreaColorBottomHistory.length - 1];
            changeBottomSafeAreaColor(bottomColor);
        },
        updateRecommendList(context, recommendList) {
            context.commit('updateRecommendList', recommendList);
        },
        updatePaywall(context, paywall) {
            context.commit('setPaywall', paywall);
        },
        updateSubscriptionPaywall(context, subscriptionPaywall) {
            context.commit('setSubscriptionPaywall', subscriptionPaywall);
        },
        setUtmReferrerData({ commit }, referrer) {
            commit('setUtmReferrer', referrer);
        },
        addRecentSearch({ commit }, query) {
            commit('ADD_RECENT_SEARCH', query);
        },
        removeRecentSearch({ commit }, query) {
            commit('REMOVE_RECENT_SEARCH', query);
        },
        clearRecentSearches({ commit }) {
            commit('CLEAR_RECENT_SEARCHES');
        },
        // 캐시 저장 action 수정
        cacheSearchResults({ commit, dispatch }, { query, results }) {
            commit('CACHE_SEARCH_RESULTS', { query, results });
            // 캐시 저장 후 만료된 항목 자동 정리
            dispatch('clearExpiredCache');
        },
        // 만료된 캐시 정리 action 추가
        clearExpiredCache({ commit }) {
            commit('CLEAR_EXPIRED_CACHE');
        },
        // 앱 시작 시 호출할 초기화 action
        initializeStore({ dispatch }) {
            // 앱 시작 시 만료된 캐시 정리
            dispatch('clearExpiredCache');

            // 히스토리 캐시도 확인
            const state = this.state;
            if (state.historyDataCache.timestamp &&
                Date.now() - state.historyDataCache.timestamp > CACHE_EXPIRY_TIME) {
                dispatch('clearHistoryCache');
            }

            // 주기적으로 만료된 캐시 정리 (5분마다)
            setInterval(() => {
                dispatch('clearExpiredCache');
            }, CACHE_EXPIRY_TIME);
        },
        async fetchHistoryData({ commit, state }, { phoneNumber, userId, forceRefresh = false }) {
            // 캐시가 유효하고 강제 갱신이 아니면 캐시 사용
            if (!forceRefresh &&
                state.historyDataCache.userId === userId &&
                state.historyDataCache.timestamp &&
                Date.now() - state.historyDataCache.timestamp < CACHE_EXPIRY_TIME) {
                return state.historyData;
            }

            commit('SET_HISTORY_LOADING', true);
            try {
                const { getUserBoxHistory } = await import('@/tarot_box/helper/mytarot_api');
                const res = await getUserBoxHistory(phoneNumber, userId);

                if (res.result === 'success') {
                    commit('SET_HISTORY_DATA', { data: res.data, userId });
                    return res.data;
                }
                return [];
            } catch (error) {
                console.error('Failed to fetch history data:', error);
                // 에러 시 캐시된 데이터 반환
                return state.historyData || [];
            } finally {
                commit('SET_HISTORY_LOADING', false);
            }
        },

        restoreHistoryFromCache({ commit }, userId) {
            commit('RESTORE_HISTORY_FROM_CACHE', userId);
        },

        updateHistoryItem({ commit }, updatedItem) {
            commit('UPDATE_HISTORY_ITEM', updatedItem);
        },

        removeHistoryItem({ commit }, itemId) {
            commit('REMOVE_HISTORY_ITEM', itemId);
        },

        clearHistoryCache({ commit }) {
            commit('CLEAR_HISTORY_CACHE');
        },
    },
    getters: {
        user: (state) => state.user,
        myPageUserInfo: state => state.myPageUserInfo,
        feedbackBoxVisible: (state) => state.feedbackBoxVisible,
        tarotBoxes: (state) => state.tarot_boxes,
        promotionBanners: (state) => state.promotion_banners,
        iconCategories: (state) => state.icon_categories,
        homeSections: (state) => state.home_sections,
        rankingSubjectTitles: (state) => state.ranking_subject_titles,
        searchBarContents: (state) => state.searchBarContents,
        safeAreaColorTopHistory: (state) => state.safeAreaColorTopHistory,
        safeAreaColorBottomHistory: (state) => state.safeAreaColorBottomHistory,
        hasTarotBoxes: state => state.tarot_boxes.length > 0,
        tempInputData: state => state.tempInputData,
        tempCardData: state => state.tempCardData,
        paywall: (state) => state.paywall,
        subscriptionPaywall: (state) => state.subscriptionPaywall,
        getSubscriptionPrices: (state) => (osType, avatarIndex) => {
            const platform = osType.toLowerCase() === 'ios' ? 'ios' : 'android';
            const group = avatarIndex % 4;

            const monthlyProduct = state.subscriptionPaywall[platform].find(
                product => product.group === group && product.period === 'monthly'
            );

            const yearlyProduct = state.subscriptionPaywall[platform].find(
                product => product.group === group && product.period === 'yearly'
            );

            return {
                monthly: monthlyProduct ? { price: monthlyProduct.price, product_id: monthlyProduct.product_id } : null,
                yearly: yearlyProduct ? { price: yearlyProduct.price, product_id: yearlyProduct.product_id } : null
            };
        },
        utmReferrer: (state) => state.utmReferrer,
        recentSearches: state => state.recentSearches,
        // 캐시 조회 getter 수정 - 만료 시간 확인 추가
        cachedSearchResult: (state) => query => {
            const cacheItem = state.cachedSearches[query];

            // 캐시 항목이 없으면 null 반환
            if (!cacheItem) {
                return null;
            }

            // 캐시 만료 시간 확인
            const now = Date.now();
            const isExpired = now - cacheItem.timestamp > CACHE_EXPIRY_TIME;

            // 만료된 경우 해당 캐시 항목을 제거하는 로직
            if (isExpired) {
                // 다음 tick에서 캐시 정리 액션 실행 (비동기)
                setTimeout(() => {
                    this.dispatch('clearExpiredCache');
                }, 0);
                return null;
            }

            return cacheItem.results;
        },
        historyData: state => state.historyData,
        historyLoading: state => state.historyLoading,
        isHistoryCacheValid: state => userId => {
            return state.historyDataCache.userId === userId &&
                state.historyDataCache.timestamp &&
                Date.now() - state.historyDataCache.timestamp < CACHE_EXPIRY_TIME;
        },
        filteredHistoryData: state => (reviewMode = false) => {
            let filtered = state.historyData.filter(item => item.status !== 'error');

            if (reviewMode) {
                filtered = filtered.filter(item => item.feedback_reward_status === 'available');
            }

            return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        },
        historyTotalReviewRewards: state => {
            return state.historyData.reduce((acc, item) => {
                if (item.feedback_reward_status === 'available') {
                    return acc + item.feedback_reward;
                }
                return acc;
            }, 0);
        },
    },
    plugins: [
        VuexPersistedstate(),
        store => {
            // 앱 초기화 시 캐시 정리 로직 실행
            store.dispatch('initializeStore');
        }
    ]
});